import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

/* ---------------------------------------------------------------------------
   SEO / social metadata for a client-rendered app.

   • public/index.html carries the complete static set (title, description,
     Open Graph, Twitter card, canonical, JSON-LD) — that is what link
     scrapers (Slack, LinkedIn, X, Facebook, iMessage) read, since they do not
     run JavaScript.
   • <PageMeta/> (mounted once inside the router) rewrites the same tags per
     route for browsers and for Google, which does render JavaScript: tab
     titles, per-page descriptions, canonical URLs and robots directives.
   • Tenant / owner surfaces (start page, offer, sign, onboarding) are private
     or white-label, so they are `noindex` and keep their tenant titles — those
     pages set document.title themselves once the organisation has loaded.

   One constant decides the public origin: REACT_APP_SITE_URL (frontend/.env).
   It falls back to the current origin, so preview and production both work.
--------------------------------------------------------------------------- */

export const SITE = {
  name: "Listed by DigiHome",
  short: "Listed",
  publisher: "DigiHome",
  locale: "en_GB",
  origin: (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, ""),
  ogImage: "/og/listed-og.png",
  ogImageAlt: "Listed by DigiHome — Win new owners from your own website.",
  themeColor: "#F3F1EC",
};

export const DEFAULT_META = {
  title: "Listed by DigiHome — Win new owners from your own website",
  description:
    "Listed puts an instant rental estimate on your website, in your brand, and takes every owner enquiry through offer, e-signature and onboarding to a live listing in your PMS. For holiday-rental property managers. Free until your first owner signs.",
  robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  type: "website",
};

/** Route → metadata. Order matters: first match wins. `canonical: false` = no canonical tag. */
const ROUTES = [
  { path: "/", title: DEFAULT_META.title, description: DEFAULT_META.description },
  {
    path: "/pricing",
    title: "Pricing — Free until you sign an owner · Listed by DigiHome",
    description:
      "No monthly fee, no seats, no setup. Run every enquiry, estimate and offer through Listed and pay one flat amount only when an owner signs on the platform.",
  },
  {
    path: "/early-access",
    title: "Early access — Win more owners, with an offer they sign on their phone · Listed by DigiHome",
    description:
      "Listed turns an owner enquiry on your website into a branded offer, a signed agreement and a property ready for your PMS. Free until you sign an owner — no setup fee, no subscription. We're onboarding our first 20 property managers personally. Apply for early access.",
  },
  { path: "/waitlist", title: "Early access · Listed by DigiHome", description: "Apply for early access to Listed.", robots: "noindex, follow", canonical: false },
  { path: "/privacy", title: "Privacy policy · Listed by DigiHome", description: "How Listed handles personal data for property managers, property owners and website visitors — roles, purposes, sub-processors, retention, cookies and your rights." },
  { path: "/terms", title: "Terms of service · Listed by DigiHome", description: "The agreement between DigiHome and the property-management companies that use Listed: the service, fees per signed owner, owner data, e-signatures, integrations and liability." },
  {
    path: "/for/:domain",
    title: ({ domain }) => `Listed for ${domain} — your website, winning owners`,
    description: "A personalised preview of Listed in your brand: instant estimate, offer, e-signature and onboarding, straight from your own website.",
    robots: "noindex, nofollow",
    canonical: false,
  },
  { path: "/preview/:token", title: "Your Listed preview", description: "A personalised preview of Listed in your brand.", robots: "noindex, nofollow", canonical: false },
  { path: "/start/:slug", title: "Get your free rental estimate", description: "Find out what your holiday home could earn — an instant estimate, then a clear offer and a simple e-signature.", robots: "noindex, follow", canonical: false },
  { path: "/w/:slug", title: "Free rental estimate", description: "Instant estimate for your holiday home.", robots: "noindex, nofollow", canonical: false },
  { path: "/offer/:token", title: "Your offer", description: "Review and accept your management offer.", robots: "noindex, nofollow", canonical: false },
  { path: "/sign/:token", title: "Sign your agreement", description: "Review and sign your management agreement online.", robots: "noindex, nofollow", canonical: false },
  { path: "/onboard/:token", title: "Onboarding", description: "Tell us about your home so we can get it live.", robots: "noindex, nofollow", canonical: false },
  { path: "/app/login", title: "Log in · Listed by DigiHome", description: "Log in to your Listed workspace — leads, estimates, offers, agreements and onboarding in one place." },
  { path: "/app/register", title: "Create your workspace · Listed by DigiHome", description: "Set up Listed for your property-management company in minutes. Free until your first owner signs." },
  { path: "/app/pipeline", title: "Pipeline · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/outbox", title: "Outbox · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/settings/*", title: "Settings · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/settings", title: "Settings · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/leads/:leadId", title: "Lead · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/offers/:offerId", title: "Offer builder · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app/*", title: "Dashboard · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/app", title: "Dashboard · Listed", robots: "noindex, nofollow", canonical: false },
  { path: "/hq/*", title: "Listed HQ", robots: "noindex, nofollow", canonical: false },
  { path: "/hq", title: "Listed HQ", robots: "noindex, nofollow", canonical: false },
];

export function resolveMeta(pathname) {
  for (const r of ROUTES) {
    const m = matchPath({ path: r.path, end: true }, pathname);
    if (!m) continue;
    const params = m.params || {};
    const title = typeof r.title === "function" ? r.title(params) : r.title;
    const description = typeof r.description === "function" ? r.description(params) : r.description || DEFAULT_META.description;
    const canonical = r.canonical === false ? null : `${SITE.origin}${pathname === "/" ? "/" : pathname.replace(/\/$/, "")}`;
    return { title, description, robots: r.robots || DEFAULT_META.robots, canonical, type: r.type || DEFAULT_META.type, image: r.image || SITE.ogImage };
  }
  return { ...DEFAULT_META, canonical: null, image: SITE.ogImage };
}

/* ----- tiny head helpers (no dependency) ------------------------------------ */
function upsert(selector, create, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null) el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
  return el;
}
const meta = (name, content) => upsert(`meta[name="${name}"]`, () => Object.assign(document.createElement("meta"), { name }), { content });
const prop = (property, content) => upsert(`meta[property="${property}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", property); return m; }, { content });
function link(rel, href) {
  const existing = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (existing) existing.remove();
    return;
  }
  upsert(`link[rel="${rel}"]`, () => Object.assign(document.createElement("link"), { rel }), { href });
}

/** Write the full tag set for one page. Exposed so tenant pages can override with their brand. */
export function applyMeta({ title, description, robots, canonical, type = "website", image, url }) {
  const absImage = image ? (image.startsWith("http") ? image : `${SITE.origin}${image}`) : `${SITE.origin}${SITE.ogImage}`;
  const pageUrl = url || canonical || `${SITE.origin}${window.location.pathname}`;
  if (title) document.title = title;
  if (description) meta("description", description);
  if (robots) meta("robots", robots);
  link("canonical", canonical);
  prop("og:title", title);
  prop("og:description", description);
  prop("og:url", pageUrl);
  prop("og:type", type);
  prop("og:image", absImage);
  meta("twitter:title", title);
  meta("twitter:description", description);
  meta("twitter:image", absImage);
}

/** Mounted once inside <BrowserRouter>: keeps <head> in step with the route. */
export function PageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    applyMeta(resolveMeta(pathname));
  }, [pathname]);
  return null;
}

/** For tenant-branded surfaces: title + description + share image in the tenant's name (still noindex). */
export function useTenantMeta({ name, headline, description, image } = {}) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!name) return;
    const base = resolveMeta(pathname);
    applyMeta({ ...base, title: headline ? `${name} · ${headline}` : name, description: description || base.description, image: image || base.image });
  }, [name, headline, description, image, pathname]);
}
